import { useMemo } from "react";
import useSWR from "swr";
import { parseUnits } from "viem";

import { getServerUrl } from "config/backend";
import { USD_DECIMALS } from "config/factors";

export type AccountStats = {
  closedCount: number;
  cumsumCollateral: bigint;
  cumsumSize: bigint;
  losses: number;
  maxCapital: bigint;
  netCapital: bigint;
  realizedFees: bigint;
  realizedPnl: bigint;
  realizedPriceImpact: bigint;
  sumMaxSize: bigint;
  volume: bigint;
  wins: number;
  id: string;
};

function parseUsd(v: string | undefined | null): bigint {
  if (!v || v === "0") return 0n;
  try {
    return parseUnits(v as `${number}`, USD_DECIMALS);
  } catch (_parseUnitsError) {
    try {
      return BigInt(v);
    } catch (_bigIntError) {
      return 0n;
    }
  }
}

/**
 * MIGRATED: Replaced Subgraph query with /accounts/:account/pnl_summary REST endpoint.
 * Uses the "all" bucket for all-time stats.
 */
export function useAccountStats(chainId: number, params: { account?: string; enabled?: boolean }) {
  const { account, enabled = true } = params;

  const { data, error, isLoading } = useSWR<AccountStats | undefined>(
    enabled && account ? ["useAccountStats", chainId, account] : null,
    {
      fetcher: async () => {
        try {
          const url = getServerUrl(chainId, `/accounts/${account}/pnl_summary`);
          const response = await fetch(url);
          const json = await response.json();

          // Backend returns { points: [{ bucketLabel, pnlUsd, volume, wins, losses, ... }] }
          const allRow = json?.points?.find((p: any) => p.bucketLabel === "all") ?? json?.points?.[0];
          if (!allRow) return undefined;

          return {
            closedCount: (allRow.wins ?? 0) + (allRow.losses ?? 0),
            cumsumCollateral: 0n,
            cumsumSize: 0n,
            losses: allRow.losses ?? 0,
            maxCapital: 0n,
            netCapital: parseUsd(allRow.pnlUsd),
            realizedFees: 0n,
            realizedPnl: parseUsd(allRow.pnlUsd),
            realizedPriceImpact: 0n,
            sumMaxSize: 0n,
            volume: parseUsd(allRow.volume),
            wins: allRow.wins ?? 0,
            id: account!,
          };
        } catch (e) {
          console.error("Failed to fetch account stats", e);
          return undefined;
        }
      },
    }
  );

  return useMemo(() => ({ data, error, loading: isLoading }), [data, error, isLoading]);
}
